import { TextSelectionSearchWrapper } from '@components/common/TextSelectionSearchPopover';
import ScrollbarTranscript from '@components/custom-scroll/ScrollbarTranscript';
import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import NoAnalysis from '@components/dynamic-survey/details/NoAnalysis';
import ErrorBoundary from '@components/ErrorBoundary';
import { CustomTooltip } from '@components/shared/CustomTooltip';
import Spinner from '@components/spinner/Spinner';
import { ALL_LENSES } from '@constants/analysisLenses';
import { useMixpanel } from '@context/MixpanelProvider';
import { useDeleteAnalysisItem } from '@hooks/deleteQueries';
import { useAnalysisItemDetail } from '@hooks/useAnalysisItemDetail';
import { useAnalysisItems } from '@hooks/useAnalysisItems';
import { MoreHoriz } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useQueryClient } from '@tanstack/react-query';
import { ANALYSIS_LENS_EVENTS } from '@utils/eventSchema';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CustomFrameworkAnalysis from '@components/interview/analysis-components/CustomFrameworkAnalysis';
import FramingDiscourseAnalysis from '@components/interview/analysis-components/FramingDiscourseAnalysis';
import GroundedEmergenceAnalysis from '@components/interview/analysis-components/GroundedEmergenceAnalysis';
import JourneyPainAnalysis from '@components/interview/analysis-components/JourneyPainAnalysis';
import JtbdMapAnalysis from '@components/interview/analysis-components/JtbdMapAnalysis';
import NarrativeArcAnalysis from '@components/interview/analysis-components/NarrativeArcAnalysis';
import OutcomeBarrierAnalysis from '@components/interview/analysis-components/OutcomeBarrierAnalysis';
import PersonaTaskAnalysis from '@components/interview/analysis-components/PersonaTaskAnalysis';
import PhenomenologicalAnalysis from '@components/interview/analysis-components/PhenomenologicalAnalysis';
import ReflexivityTrackerAnalysis from '@components/interview/analysis-components/ReflexivityTrackerAnalysis';
import ResonanceScanAnalysis from '@components/interview/analysis-components/ResonanceScanAnalysis';
import SaturationGaugeAnalysis from '@components/interview/analysis-components/SaturationGaugeAnalysis';
import SentimentSpectrumAnalysis from '@components/interview/analysis-components/SentimentSpectrumAnalysis';
import TocAlignmentAnalysis from '@components/interview/analysis-components/TocAlignmentAnalysis';
import AnalysisReport from './generic-analysis-report';
import EquityAuditAnalysis from '@components/interview/analysis-components/EquityAuditAnalysis';

// map every lens ID → its component
const analysisComponents = {
  grounded_emergence: GroundedEmergenceAnalysis,
  custom_lens: CustomFrameworkAnalysis,
  phenomenological: PhenomenologicalAnalysis,
  narrative_arc: NarrativeArcAnalysis,
  jtbd_map: JtbdMapAnalysis,
  journey_pain: JourneyPainAnalysis,
  persona_task: PersonaTaskAnalysis,
  sentiment_spectrum: SentimentSpectrumAnalysis,
  framing_discourse: FramingDiscourseAnalysis,
  resonance_scan: ResonanceScanAnalysis,
  outcome_barrier: OutcomeBarrierAnalysis,
  equity_audit: EquityAuditAnalysis,
  toc_alignment: TocAlignmentAnalysis,
  saturation_gauge: SaturationGaugeAnalysis,
  reflexivity_tracker: ReflexivityTrackerAnalysis,
};

const useStyles = makeStyles(() => ({
  sidebarContainer: {
    height: '100%',
    position: 'relative',
    background: '#ffff',
  },
  sidebar: {
    height: '100%',
    backgroundColor: '#ffff',
    borderRadius: '0 !important',
    boxShadow: 'none !important',
    borderRight: '1px solid #e8e8e8',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const AnalyzedListItem = ({
  analyses,
  handleAnalysisSelect,
  selectedIndex,
  handleDeleteAnalysisItem,
}) => {
  const classes = useStyles();
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);

  return (
    <ScrollbarTranscript className={classes.sidebarContainer} height="calc(100vh - 135px)">
      <List sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {analyses
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .map((item, index) => {
            const { analysis_item_id, created_at, analysis_lens_type, customer_member_id } = item;

            const lens = ALL_LENSES.find((l) => l.id === analysis_lens_type);
            const primary = lens?.name || analysis_lens_type || '(unset)';
            const meta = [`Created: ${new Date(created_at).toLocaleString()}`];
            if (customer_member_id) meta.push(`Member: ${customer_member_id}`);
            return (
              <ListItem key={analysis_item_id}>
                <CustomTooltip
                  key={analysis_item_id}
                  title={lens?.description || ''}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    disableRipple
                    selected={selectedIndex === index}
                    onClick={() => {
                      handleAnalysisSelect(item, index);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 2,
                      border:
                        selectedIndex === index ? '2px solid #7D5AE8' : '2px solid transparent',
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <ListItemText
                        primary={<Typography fontWeight={600}>{primary}</Typography>}
                        style={{ fontWeight: 600 }}
                        secondary={<Typography variant="caption">{meta.join(' | ')}</Typography>}
                      />
                    </Box>
                    <IconButton
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteAnchorEl(e.currentTarget);
                      }}
                    >
                      <MoreHoriz />
                    </IconButton>
                    <Menu
                      id="delete-menu"
                      anchorEl={deleteAnchorEl}
                      open={Boolean(deleteAnchorEl)}
                      onClose={() => setDeleteAnchorEl(null)}
                    >
                      <MenuItem onClick={() => handleDeleteAnalysisItem(analysis_item_id)}>
                        Delete
                      </MenuItem>
                    </Menu>
                  </ListItemButton>
                </CustomTooltip>
              </ListItem>
            );
          })}
      </List>
    </ScrollbarTranscript>
  );
};
function YourAnalysesContainer() {
  const { id } = useParams();
  const { trackEvent } = useMixpanel();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const indexs = parseInt(searchParams.get('selected') || '0', 10);

  // const [selectedIndex, setSelectedIndex] = useState(indexs);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // fetch analysis items; default to empty list until data arrives
  const { data: analyses = [], isPending, isError } = useAnalysisItems(id);
  const deleteAnalysisItem = useDeleteAnalysisItem();

  // selected item summary and details
  const selectedSummary = analyses[indexs];
  const selectedItemId = selectedSummary?.analysis_item_id;

  const {
    data: analysisDetail,
    isLoading: isLoadingDetail,
    isError: isDetailError,
  } = useAnalysisItemDetail(id, selectedItemId);

  // Track when user views an analysis result
  const handleAnalysisSelect = useCallback(
    (analysis, index) => {
      navigate(`?selected=${index}`);
      trackEvent('analysisLens', {
        ...ANALYSIS_LENS_EVENTS.LENS_RESULTS_VIEWED,
        study_id: id,
        lens_id: analysis.analysis_lens_type,
        analysis_item_id: analysis.analysis_item_id,
        created_at: analysis.created_at,
      });
    },
    [id],
  );

  const handleConfirmDeleteAnalysisItem = () => {
    deleteAnalysisItem.mutate(
      { studyId: id, analysisItemId: selectedItemId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['analysisItems', id] });

          setShowDeleteConfirm(false);
        },
      },
      {
        onError: (error) => {
          if (error?.response?.status === 403) {
            toast.error('You do not have access to delete analysis items for your role');
          } else {
            toast.error('Error deleting analysis item:', error);
          }
        },
      },
    );
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteConfirm(true);
  };

  //render sidebar list
  const renderList = useMemo(() => {
    return (
      <AnalyzedListItem
        analyses={analyses}
        handleAnalysisSelect={handleAnalysisSelect}
        selectedIndex={indexs}
        handleDeleteAnalysisItem={handleDeleteConfirm}
      />
    );
  }, [analyses, handleAnalysisSelect, indexs]);

  if (isPending) {
    return <Spinner />;
  }

  if (analyses.length === 0) {
    return <NoAnalysis />;
  }

  if (isError) {
    return <NoAnalysis />;
  }

  return (
    <Grid
      container
      sx={{
        height: 'calc(100vh - 125px)',
        overflow: 'hidden',
      }}
    >
      {/* Transcript list */}
      <Grid
        item
        xs={12}
        md={12}
        lg={2.5}
        sx={{
          height: '100%',
          borderRight: '1px solid #e8e8e8',
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0 }}>{renderList}</Box>
        </Box>
      </Grid>

      {/* Main content */}
      <Grid
        item
        xs={12}
        md={12}
        lg={9.5}
        sx={{
          height: '100%',
          overflow: 'auto',
        }}
      >
        <Grid container>
          {/* Main Content */}
          <Grid item xs={12} md={12}>
            <Box p={5}>
              <TextSelectionSearchWrapper>
                {isLoadingDetail ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress />
                  </Box>
                ) : isDetailError ? (
                  <Typography color="error" sx={{ p: 2 }}>
                    Analysis item not found.
                  </Typography>
                ) : analysisDetail ? (
                  (() => {
                    const Component =
                      analysisComponents[analysisDetail.analysis_lens_type] || AnalysisReport;

                    return <Component item={analysisDetail} />;
                  })()
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No results to display.
                  </Typography>
                )}
              </TextSelectionSearchWrapper>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <DeleteConfirmationDialog
        open={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleConfirmDeleteAnalysisItem}
        isPending={false}
        title={`Delete Analysis`}
        okText={'Delete'}
        noText={'Cancel'}
        content={
          <Box>
            <Typography>
              Are you sure you want to delete this analysis? This action cannot be undone.
            </Typography>
          </Box>
        }
      />
    </Grid>
  );
}

const YourAnalyses = () => {
  return (
    <ErrorBoundary>
      <YourAnalysesContainer />
    </ErrorBoundary>
  );
};

export default YourAnalyses;
